import React, { useState } from "react";
import { t } from "i18next";
import {
  Box,
  Divider,
  IconButton,
  Popover,
  Stack,
  Typography,
} from "@mui/material";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import FlagIcon from "@mui/icons-material/Flag";
import { Question, Cue } from "../../shared/interfaces/GameLogicTypes";
import { Socket } from "socket.io-client";
import { renderCueContent } from "../../shared/helper/renderCueContent";

type Action = "like" | "dislike" | "report";

interface ReportPanelProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  question: Question;
  cues: Cue[];
  socket: Socket;
  isTsar: boolean;
  tsarName: string;
}

function cleanQuestionText(text: string, tsarName: string): string {
  return text
    .replace(/<\/?h>/g, "")
    .replace(/\{\{tsar\}\}/g, tsarName);
}

export default function ReportPanel({
  open,
  anchorEl,
  onClose,
  question,
  cues,
  socket,
  isTsar,
  tsarName,
}: ReportPanelProps) {
  const [ratings, setRatings] = useState<Record<string, Action>>({});

  const handleRate = (
    cardType: "question" | "answer",
    cardId: number | undefined,
    action: Action
  ) => {
    if (!cardId) return;
    const key = `${cardType}_${cardId}`;
    if (ratings[key] === action) {
      // Toggle off — undo the rating
      setRatings((prev) => { const next = { ...prev }; delete next[key]; return next; });
      socket.emit("unrateCard", { cardType, cardId, action });
    } else if (!ratings[key]) {
      // Not yet rated — apply rating
      setRatings((prev) => ({ ...prev, [key]: action }));
      socket.emit("rateCard", { cardType, cardId, action });
    }
    // If rated differently, do nothing (can't switch, must undo first)
  };

  const questionText = cleanQuestionText(
    isTsar ? question.forTsar : question.aboutTsar,
    tsarName
  );

  const renderActions = (
    cardType: "question" | "answer",
    cardId: number | undefined
  ) => {
    const key = `${cardType}_${cardId}`;
    const current = ratings[key];
    return (
      <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
        <IconButton
          size="small"
          onClick={() => handleRate(cardType, cardId, "like")}
          disabled={!!current && current !== "like"}
          sx={{ color: current === "like" ? "#4caf50" : undefined }}
        >
          {current === "like" ? (
            <ThumbUpIcon fontSize="small" />
          ) : (
            <ThumbUpOutlinedIcon fontSize="small" />
          )}
        </IconButton>
        <IconButton
          size="small"
          onClick={() => handleRate(cardType, cardId, "dislike")}
          disabled={!!current && current !== "dislike"}
          sx={{ color: current === "dislike" ? "#ff9800" : undefined }}
        >
          {current === "dislike" ? (
            <ThumbDownIcon fontSize="small" />
          ) : (
            <ThumbDownOutlinedIcon fontSize="small" />
          )}
        </IconButton>
        <IconButton
          size="small"
          onClick={() => handleRate(cardType, cardId, "report")}
          disabled={!!current && current !== "report"}
          sx={{ color: current === "report" ? "#f44336" : undefined }}
        >
          {current === "report" ? (
            <FlagIcon fontSize="small" />
          ) : (
            <FlagOutlinedIcon fontSize="small" />
          )}
        </IconButton>
      </Stack>
    );
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <Box sx={{ width: { xs: "85vw", sm: 340 }, p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          {t("report_panel_title")}
        </Typography>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{ mb: 1 }}
        >
          <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
            {questionText}
          </Typography>
          {renderActions("question", question.id)}
        </Stack>

        <Divider sx={{ my: 1 }} />

        <Stack spacing={0.75}>
          {cues.map((cue, idx) => (
            <Stack
              key={idx}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
            >
              <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                {renderCueContent(String(cue.content))}
              </Typography>
              {renderActions("answer", cue.answerCardId)}
            </Stack>
          ))}
        </Stack>
      </Box>
    </Popover>
  );
}
